import { useEffect, useMemo, useState } from 'react';

import { LoadingSpinner } from './LoadingSpinner';

export interface JsonViewerTab {
  /** Stable identifier used as React key. */
  id: string;
  /** Tab label shown in the header. */
  label: string;
  /** When provided, the modal renders a spinner instead of `value`. */
  isLoading?: boolean;
  /** When provided, the modal renders the error message instead of `value`. */
  errorMessage?: string;
  /** Pretty-printed string OR an arbitrary value that will be `JSON.stringify`ed. */
  value: unknown;
}

export interface JsonViewerModalProps {
  /** Whether the modal is visible. Pass `false` to hide. */
  open: boolean;
  /** Closes the modal. Triggered by Escape, backdrop click and the explicit close button. */
  onClose: () => void;
  /** Header title. */
  title: string;
  /** Optional short subtitle/description. */
  subtitle?: string;
  /** Tabs to render. Must contain at least one. The first one is selected by default. */
  tabs: JsonViewerTab[];
}

function formatValue(value: unknown): string {
  if (typeof value === 'string') {
    // The raw AI tab may already be a JSON-encoded string; try to pretty-print it.
    try {
      const parsed = JSON.parse(value);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return value;
    }
  }
  if (value === null || value === undefined) {
    return '';
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/**
 * Generic JSON inspector modal with switchable tabs. Used by the discovery review/publish
 * screens to surface either the normalised proposal or the raw model response. Caller is
 * responsible for gating which tabs are visible (e.g. only include the "raw" tab when the
 * principal has the platform:debug scope).
 */
export function JsonViewerModal({
  open,
  onClose,
  title,
  subtitle,
  tabs,
}: JsonViewerModalProps): JSX.Element | null {
  const [activeId, setActiveId] = useState<string>(tabs[0]?.id ?? '');

  // Reset the active tab when the modal re-opens or the tab list changes shape.
  useEffect(() => {
    if (open) {
      setActiveId(tabs[0]?.id ?? '');
    }
  }, [open, tabs]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const active = useMemo(() => tabs.find((t) => t.id === activeId) ?? tabs[0], [tabs, activeId]);
  const formatted = useMemo(() => (active ? formatValue(active.value) : ''), [active]);

  if (!open || tabs.length === 0 || active === undefined) {
    return null;
  }

  const handleCopy = async (): Promise<void> => {
    if (typeof navigator === 'undefined' || navigator.clipboard === undefined) {
      return;
    }
    try {
      await navigator.clipboard.writeText(formatted);
    } catch {
      // Clipboard permission denied — no fallback needed, the textarea is still selectable.
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="json-viewer-title"
      className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="card flex h-[80vh] w-full max-w-4xl flex-col p-4 sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id="json-viewer-title" className="text-lg font-semibold text-slate-900">
              {title}
            </h2>
            {subtitle !== undefined ? (
              <p className="mt-1 text-sm text-slate-600">{subtitle}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
            aria-label="Cerrar"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5">
              <path d="M6 6l12 12M6 18L18 6" />
            </svg>
          </button>
        </div>

        {tabs.length > 1 ? (
          <div role="tablist" className="mt-4 flex gap-1 border-b border-slate-200">
            {tabs.map((tab) => {
              const isActive = tab.id === active.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={
                    isActive
                      ? 'border-b-2 border-sky-500 px-3 py-2 text-sm font-medium text-sky-700'
                      : 'border-b-2 border-transparent px-3 py-2 text-sm font-medium text-slate-500 hover:text-slate-700'
                  }
                  onClick={() => setActiveId(tab.id)}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        ) : null}

        <div className="mt-3 flex flex-1 flex-col overflow-hidden">
          {active.isLoading === true ? (
            <div className="flex h-full items-center justify-center">
              <LoadingSpinner />
            </div>
          ) : active.errorMessage !== undefined ? (
            <div className="rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
              {active.errorMessage}
            </div>
          ) : (
            <pre className="flex-1 overflow-auto rounded border border-slate-200 bg-slate-50 p-3 font-mono text-xs leading-snug text-slate-800">
              {formatted}
            </pre>
          )}
        </div>

        <div className="mt-3 flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={handleCopy}>
            Copiar al portapapeles
          </button>
          <button type="button" className="btn-primary" onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
